import { useEffect, useRef, useState } from "react";
import { Download, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DayField } from "@/components/DayField";
import { api, apiError, downloadFile } from "@/lib/api";

const EMPTY = {
  id: null,
  kind: "",
  title: "",
  plant: "",
  mode: "Offline",
  venue: "",
  from_date: "",
  to_date: "",
  faculty: "",
  organiser: "",
  remarks: "",
};

const Field = ({ label, hint, wide, children }) => (
  <div className={wide ? "sm:col-span-2" : ""}>
    <label className="label-caps">{label}</label>
    <div className="mt-1">{children}</div>
    {hint && <p className="mt-1 text-[10px] text-muted-foreground">{hint}</p>}
  </div>
);

/** One training a unit ran — the thing the attendance then hangs off. */
export const ProgrammeFormDialog = ({ open, onOpenChange, programme, types = [], modes = [],
                                      plants = [], myPlant = "", past = false, onSaved }) => {
  const picker = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [roster, setRoster] = useState(null);

  useEffect(() => {
    if (!open) return;
    setRoster(null);
    setForm({
      ...EMPTY,
      kind: types[1] || types[0] || "",
      plant: myPlant || "",
      ...(programme || {}),
    });
  }, [open, programme, myPlant, types]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const pullRoster = async () => {
    setBusy(true);
    try {
      await downloadFile("/training/roster-template", {}, "past_training_roster.xlsx");
      toast.success("Fill the roster and attach it here — one row per participant.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!form.kind || !form.title.trim() || !form.plant) {
      toast.error("The type, the name and the unit are needed.");
      return;
    }
    setBusy(true);
    const body = {
      kind: form.kind,
      title: form.title.trim(),
      plant: form.plant,
      mode: form.mode,
      venue: form.venue || "",
      from_date: form.from_date || "",
      to_date: form.to_date || "",
      faculty: form.faculty || "",
      organiser: form.organiser || "",
      remarks: form.remarks || "",
    };
    try {
      let id = form.id;
      if (id) await api.patch(`/training/programmes/${id}`, body);
      else {
        const { data } = await api.post("/training/programmes", body);
        id = data.id;
      }
      if (roster) {
        const sheet = new FormData();
        sheet.append("file", roster);
        const { data: said } = await api.post(
          `/training/programmes/${id}/participants/import?apply=true`,
          sheet,
        );
        toast.success(
          `${body.title} loaded — ${said.applied.added} participant(s) added, ` +
            `${said.applied.marks} attendance mark(s) set.`,
        );
      } else {
        toast.success(form.id ? "Programme corrected." : `${body.title} is on the register.`);
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[88vh] overflow-y-auto rounded-none bg-card sm:max-w-2xl"
        data-testid="programme-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            {form.id
              ? "Correct this training"
              : past
              ? "Load a training already held"
              : "Add a training"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {past
              ? "Fill in what the training was, then attach the roster — Unit, SAIL number, name, " +
                "designation, department, e-mail and phone, exactly as your nomination list " +
                "carries them. Add a column per day (heading 15.06.2026, value F / H / A) and the " +
                "attendance comes in with it."
              : "Coursera comes from the tracker. Everything a unit or SDTD arranges itself — a " +
                "classroom session, an institute course, a webinar, a workshop or a site visit — " +
                "is kept here, with the hours worked out from the day-wise attendance."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Type of training *">
            <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
              <SelectTrigger className="h-11 rounded-none" data-testid="pg-kind">
                <SelectValue placeholder="Pick the type" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-popover">
                {types.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Unit *">
            <Select value={form.plant} onValueChange={(v) => set("plant", v)}>
              <SelectTrigger className="h-11 rounded-none" data-testid="pg-plant">
                <SelectValue placeholder="Which unit" />
              </SelectTrigger>
              <SelectContent className="max-h-72 rounded-none bg-popover">
                {plants.map((p) => (
                  <SelectItem key={p.code} value={p.code}>
                    {p.code} — {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="What it was called *" wide>
            <Input
              className="h-11 rounded-none"
              data-testid="pg-title"
              placeholder="Industrial AI for process engineers"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
            />
          </Field>
          <Field label="Mode">
            <Select value={form.mode} onValueChange={(v) => set("mode", v)}>
              <SelectTrigger className="h-11 rounded-none" data-testid="pg-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-popover">
                {modes.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Venue" hint="A hall, a plant, or the platform it ran on.">
            <Input
              className="h-11 rounded-none"
              data-testid="pg-venue"
              value={form.venue}
              onChange={(e) => set("venue", e.target.value)}
            />
          </Field>
          <Field label="From" hint="The attendance grid is built from these two dates.">
            <DayField
              tall
              testId="pg-from"
              format="iso"
              value={form.from_date}
              onChange={(v) => set("from_date", v)}
            />
          </Field>
          <Field label="To">
            <DayField
              tall
              testId="pg-to"
              format="iso"
              value={form.to_date}
              onChange={(v) => set("to_date", v)}
            />
          </Field>
          <Field label="Faculty / trainer">
            <Input
              className="h-11 rounded-none"
              data-testid="pg-faculty"
              value={form.faculty}
              onChange={(e) => set("faculty", e.target.value)}
            />
          </Field>
          <Field label="Organised by">
            <Input
              className="h-11 rounded-none"
              data-testid="pg-organiser"
              value={form.organiser}
              onChange={(e) => set("organiser", e.target.value)}
            />
          </Field>
          <Field label="Anything worth recording" wide>
            <Textarea
              rows={2}
              className="rounded-none"
              data-testid="pg-remarks"
              value={form.remarks}
              onChange={(e) => set("remarks", e.target.value)}
            />
          </Field>

          {past && !form.id && (
            <div className="sm:col-span-2 border border-border p-3" data-testid="pg-roster-block">
              <div className="label-caps text-primary">The participants of that training</div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Take the roster out, fill it, and attach it here — it is saved together with the
                programme in one go. Leave it out and you can add the participants afterwards.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 rounded-none font-display text-[10px] font-bold uppercase tracking-widest"
                  data-testid="pg-roster-template"
                  disabled={busy}
                  onClick={pullRoster}
                >
                  <Download className="mr-2 h-4 w-4" /> Roster template
                </Button>
                <input
                  ref={picker}
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  data-testid="pg-roster-file"
                  onChange={(e) => setRoster(e.target.files?.[0] || null)}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 rounded-none font-display text-[10px] font-bold uppercase tracking-widest"
                  data-testid="pg-roster-pick"
                  disabled={busy}
                  onClick={() => picker.current?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" /> Attach the roster
                </Button>
                {roster && (
                  <span className="font-data text-[11px] text-[hsl(var(--healthy))]" data-testid="pg-roster-name">
                    {roster.name}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="h-11 rounded-none"
            onClick={() => onOpenChange(false)}
          >
            Leave it
          </Button>
          <Button
            className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
            data-testid="pg-save"
            disabled={busy}
            onClick={save}
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {form.id
              ? "Save the correction"
              : roster
              ? "Load it with the roster"
              : "Put it on the register"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
